import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react';
import {ApiError, errorMessageKey, newIdempotencyKey} from '../../api';
import type {Order} from '../../api/types';
import {useRouter} from '../../app/router';
import {ROUTES, href} from '../../app/routes';
import {useI18n, type MessageKey} from '../../i18n';
import {isVerifiedPaid, orderTotalTone, useOrder} from '../../order';
import {
  attemptFor,
  isExpired,
  leaveForPayment,
  readAttemptFor,
  recordHandoff,
  recordSession,
  secondsRemaining,
  type CheckoutAttempt,
} from '../../payment';
import {useApi, useGuestSession} from '../../session';
import {
  ActionLink,
  BackLink,
  Button,
  Card,
  Money,
  Screen,
  ScreenBody,
  ScreenFooter,
  SkeletonBar,
  SplitRow,
  Stack,
  TerminalNotice,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Q8 · Pay-first checkout — route `/pay/:orderId`
 *
 * The screen that takes money, and the only screen on this surface that ends by
 * **leaving the application**.
 *
 * ## What this screen turned out to be, which is not what the spec drew
 *
 * 03 §Q8 lists "Total, methods, contact (optional), LINE loyalty opt-in, pay
 * action", and §Q9 asks for "PromptPay QR or card fields or wallet handoff, live
 * verification state". **Three of those five cannot exist here**, and each is
 * absent for a reason rather than for lack of time:
 *
 *   * **No method picker, and no QR.** ReservePay exposes no operation anywhere
 *     that returns an EMVCo payload — `retrieve-qr`, `get-qr`,
 *     `retrieve-payment` and `get-payment` all answer 404 — and no checkout
 *     response carries a payload or an image. The guest picks a method and scans
 *     a code on **ReservePay's own hosted page**, which is branded "Powered by
 *     Reservepay". pokpokPOS does not draw the moment a guest hands over money.
 *     That is a product consequence sitting on top of D-014, not a plumbing
 *     detail, and `q8.leavingNote` says it out loud rather than letting the
 *     guest discover whose page they are on after the tap.
 *   * **No contact field.** PDPA: no name, phone or email is required to order,
 *     the guest session carries none, and there is no field in the contract to
 *     put one in. A field that collects a phone number and posts it nowhere is
 *     worse than no field.
 *   * **No LINE loyalty opt-in.** Q16 is NOT DESIGNED and its substance is PDPA
 *     consent copy needing legal review. A toggle is the easy half.
 *
 * Every field is a drop-off and the ≥85% conversion guardrail is won or lost
 * here (03 §Q8), so what is left is: the amount, one sentence about what happens
 * next, and one button.
 *
 * ## What the design package draws, and what this screen does with it
 *
 * The package's `payment` screen is a single column with **no pinned footer**:
 * top bar, title, one white amount card, three paragraphs at three weights, the
 * one solid Chili Red button, two more lines, and the processor disclosure
 * pushed to the bottom of the viewport with `margin-top: auto`. That shape is
 * reproduced here — it is the design's own answer to a screen whose content is
 * short and whose one action must not float in the middle of it.
 *
 * The thing worth naming is the **triple disclosure**: the package says three
 * separate times, at three different weights, that the next page belongs to
 * ReservePay and not to the restaurant, and offers the counter as an out. It
 * reads as repetitive on a design review and is not: it is the only warning a
 * guest gets before the tab leaves the origin, and it matches the vault's
 * processor-disclosure rule exactly — plain body copy, no logo, no lockup, no
 * brand weight (D-014). All three lines survive.
 *
 * Every failure state below is drawn as a **terminal notice** rather than as red
 * text under a live button: the package's success screen is the one shape it
 * gives for "this is the end of a road", and a checkout that cannot proceed is
 * exactly that. The ring colour carries the kind — Palm Sugar where nothing is
 * broken (the venue simply does not take QR payments), Dried Chili where a
 * person now has to get involved — and the headline carries the meaning, because
 * colour never carries it alone.
 *
 * ## Pay-first, honestly: the kitchen has already fired
 *
 * The spec's purpose line is "take payment **before** the kitchen fires". This
 * screen cannot deliver that sentence and does not pretend to. A checkout needs
 * an order that exists and a `balance_due_satang` to collect, so the order is
 * submitted first — and D-025 makes the **server** fire the kitchen on submit.
 * Firing after payment instead would require the server to learn that a payment
 * succeeded, and today it cannot act on that knowledge (see Q9): the guest would
 * pay and the kitchen would never receive the ticket. Between "food arrives and
 * the bill may go unpaid" and "money is taken and no food is cooked", only one
 * of those is recoverable at a counter.
 *
 * **Consequence a product owner should see:** a QSR configured pay-first still
 * fires on submit. This is a scope decision, not a design one, and it is
 * recorded in the report rather than papered over here.
 *
 * ## Retrying is safe, and the record on disk is what makes it safe
 *
 * The `Idempotency-Key` is minted and written **before** the request, because
 * the case it protects against is the response never arriving. A retry replays
 * ReservePay's original checkout rather than creating a second one for the same
 * bill — which a guest could then pay twice, with no refund path (ReservePay
 * does not support PromptPay refunds at all).
 *
 * The one failure that is **not** retried is `upstream_timeout`. The contract's
 * own instruction is "do not retry — this order must be reconciled", because
 * ReservePay may or may not have created a checkout, and a second attempt is
 * exactly how one bill gets two. So that state has no button, only
 * `q8.reconcileTitle` and a person.
 *
 * ## Money
 *
 * Nothing here is ever Pandan while there is a balance. The figure is
 * `balance_due_satang` through `orderTotalTone`, which returns `pending` for a
 * bill that owes money and can only return `verified` for one the server
 * settled. Starting a checkout does not move it — a checkout being started is
 * not money. The single place green is reachable on this screen is the
 * already-settled branch, where the bill is zero and the server said so.
 */

/** What went wrong, in the shape this screen renders rather than as an error code. */
type Failure =
  /** 501: the deployment is not configured for hosted checkout. Dead end. */
  | {kind: 'unavailable'}
  /** The `201` carried no URL. Not a crash — the contract allows a null. */
  | {kind: 'no_redirect'}
  /** `upstream_timeout`. A checkout may exist. **No retry button.** */
  | {kind: 'reconcile'}
  /** The balance moved under us. Re-read and show the new number. */
  | {kind: 'amount_changed'}
  /** Anything else, localised from `code` — never the server's English. */
  | {kind: 'generic'; messageKey: MessageKey};

function classify(error: unknown): Failure {
  if (!(error instanceof ApiError)) return {kind: 'generic', messageKey: 'error.generic'};
  if (error.code === 'not_implemented') return {kind: 'unavailable'};
  if (error.code === 'upstream_timeout') return {kind: 'reconcile'};
  if (error.code === 'validation_failed') return {kind: 'amount_changed'};
  return {kind: 'generic', messageKey: errorMessageKey(error)};
}

export default function Q08PayFirstCheckout() {
  const {t, ln} = useI18n();
  const {params} = useRouter();
  const client = useApi();
  const {session, status: sessionStatus} = useGuestSession();
  const {order: liveOrder, loadOrder} = useOrder();

  const orderId = params['orderId'] ?? '';

  /**
   * The order this screen is billing for.
   *
   * Re-read rather than taken from `useOrder()`: the amount posted to the server
   * must equal `balance_due_satang` **as the server currently holds it**, and a
   * snapshot from before the guest walked to this screen is exactly the stale
   * number the server rejects. The provider's copy seeds the first paint so the
   * figure is not blank while the read is in flight.
   */
  const [order, setOrder] = useState<Order | null>(
    liveOrder?.order_id === orderId ? liveOrder : null,
  );
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [starting, setStarting] = useState(false);
  const [attempt, setAttempt] = useState<CheckoutAttempt | null>(() =>
    orderId ? readAttemptFor(orderId) : null,
  );

  /** Ticks the clock so a countdown driven by `expires_at` is not frozen. */
  const [now, setNow] = useState(() => Date.now());

  const refreshOrder = useCallback(() => {
    if (!orderId || sessionStatus !== 'active') return;
    setLoading(true);
    loadOrder(orderId)
      .then((loaded) => {
        setOrder(loaded);
        setFailure(null);
      })
      .catch((caught: unknown) => setFailure(classify(caught)))
      .finally(() => setLoading(false));
  }, [orderId, sessionStatus, loadOrder]);

  useEffect(refreshOrder, [refreshOrder]);

  /**
   * A guest who leaves for ReservePay and comes back to *this* screen — the back
   * gesture, or a bank app returning to the tab it left — must not be looking at
   * the balance as it was before they paid. `visibilitychange` is the only signal
   * a page gets for that on mobile Safari.
   */
  useEffect(() => {
    const onVisible = () => {
      if (globalThis.document?.visibilityState === 'visible') {
        setNow(Date.now());
        refreshOrder();
      }
    };
    globalThis.document?.addEventListener('visibilitychange', onVisible);
    return () => globalThis.document?.removeEventListener('visibilitychange', onVisible);
  }, [refreshOrder]);

  // Only while there is a live window to count down. A phone in a dining room
  // does not need a timer running against nothing.
  const live = attempt !== null && !isExpired(attempt, now) && attempt.expires_at !== null;
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [live]);

  const balanceSatang = order?.totals.balance_due_satang ?? 0;

  /**
   * Start the checkout and hand the guest over.
   *
   * `startedOnce` guards the double tap, not `starting` alone: the navigation
   * away is asynchronous in the browser, so the button survives its own click
   * for long enough to be pressed again on a laggy phone.
   */
  const startedOnce = useRef(false);
  const pay = useCallback(async () => {
    if (!order || startedOnce.current) return;
    startedOnce.current = true;
    setStarting(true);
    setFailure(null);

    // Written before the request. A key minted afterwards protects nothing,
    // because the case it exists for is the answer never arriving.
    const current = attemptFor(order.order_id, balanceSatang, newIdempotencyKey);
    setAttempt(current);

    try {
      const {session: created} = await client.createCheckoutSession(
        order.order_id,
        {amount_satang: balanceSatang},
        current.idempotency_key,
      );
      const recorded = recordSession(current, created);
      setAttempt(recorded);

      // **Do not build a client that can only handle a redirect** — the contract
      // says so, and if ReservePay's till-side SDK route is ever taken this
      // field will not be a URL. A missing one is a state with a sentence, not
      // an exception thrown at a guest holding a bill.
      if (!recorded.redirect_url) {
        setFailure({kind: 'no_redirect'});
        return;
      }

      // Written before the navigation, never after: once the tab leaves there is
      // no "after" in this document.
      recordHandoff(recorded);
      leaveForPayment(recorded.redirect_url);
    } catch (caught) {
      const classified = classify(caught);
      setFailure(classified);
      // Everything except `upstream_timeout` may be pressed again. That one may
      // not: a checkout may already exist at ReservePay and a second attempt
      // gives one bill two of them.
      if (classified.kind !== 'reconcile') startedOnce.current = false;
      if (classified.kind === 'amount_changed') refreshOrder();
    } finally {
      setStarting(false);
    }
  }, [order, balanceSatang, client, refreshOrder]);

  const remaining = useMemo(
    () => (attempt ? secondsRemaining(attempt, now) : null),
    [attempt, now],
  );

  const venue = ln(session?.merchant_display_name);

  const topBar = (
    <TopBar
      leading={<BackLink to={ROUTES.status} label={t('common.back')} />}
      trailing={<LanguageToggle variant="link" />}
    />
  );

  /**
   * The ordinary shell: title, content, and the processor disclosure held at the
   * foot of the viewport by `margin-top: auto` — which is how the package draws
   * it, and the reason this screen has no `ScreenFooter`. There is nothing to
   * pin: the content is short and the one action belongs in the flow of the
   * copy that explains it, not in a bar detached from it.
   */
  const frame = (children: ReactNode) => (
    <Screen>
      <ScreenStyles />
      {topBar}
      <ScreenBody className="q8-body">
        <div className="pk-stack">
          <h1 className="pk-title">{t('q8.title')}</h1>
          {/* The venue names itself on the one screen where the guest is about
              to hand money to somebody. The package does not draw it here; a
              payment page that does not say who is being paid is worse. */}
          {venue ? <p className="pk-muted">{venue}</p> : null}
        </div>
        {children}
        {/*
          Processor disclosure — body copy at the foot, never brand-weighted and
          nowhere near the wordmark's position (D-014, foundations §7).
        */}
        <p className="pk-hint q8-foot">{t('q17.processorDisclosure')}</p>
      </ScreenBody>
    </Screen>
  );

  /**
   * The end of a road, in the package's own vocabulary for one: a ring, a
   * headline, body copy, and the ways out in a footer with no rule above it.
   *
   * **No solid red in any of these.** The red rule is one forward action per
   * screen and none of these states has one — a red button that re-attempts a
   * checkout the contract says not to re-attempt is a promise the screen cannot
   * keep.
   */
  const notice = (
    tone: 'warning' | 'error',
    glyph: string,
    title: string,
    body: ReactNode,
    actions?: ReactNode,
  ) => (
    <Screen>
      <ScreenStyles />
      {topBar}
      <ScreenBody center>
        <TerminalNotice tone={tone} glyph={glyph} title={title}>
          {body}
        </TerminalNotice>
      </ScreenBody>
      <ScreenFooter bordered={false}>
        {actions}
        <p className="pk-hint q8-center">{t('q17.processorDisclosure')}</p>
      </ScreenFooter>
    </Screen>
  );

  const backToBill = (
    <ActionLink to={href(ROUTES.receipt, {orderId})} variant="tertiary" block>
      {t('q8.backToBill')}
    </ActionLink>
  );

  // --- no credential --------------------------------------------------------

  if (sessionStatus === 'idle' || sessionStatus === 'expired' || sessionStatus === 'invalid') {
    return notice(
      'error',
      '!',
      t('error.session'),
      <p className="pk-muted">{t('common.askStaff')}</p>,
      <ActionLink to={ROUTES.invalid} variant="secondary" block>
        {t('q19.rescan')}
      </ActionLink>,
    );
  }

  // --- the deployment cannot take a payment at all --------------------------
  //
  // An honest dead end, exactly as 03 §Q9 decided for a venue that has stopped
  // accepting QR orders: no retry spinner, no queue, no "try again shortly"
  // that resolves into nothing. A hopeful dead end keeps a guest tapping
  // instead of waving. Palm Sugar rather than Dried Chili — nothing is broken,
  // this venue simply settles at the counter.

  if (failure?.kind === 'unavailable') {
    return notice(
      'warning',
      '!',
      t('q8.unavailableTitle'),
      <>
        <p>{t('q17.payAtCounter')}</p>
        <p className="pk-muted">{t('common.askStaff')}</p>
      </>,
      backToBill,
    );
  }

  // --- a checkout with no page to send anybody to ---------------------------
  //
  // A dead end, not a retry. The contract's warning — "do not build a client
  // that can only handle a redirect" — is honoured by not crashing, not by
  // pretending this client can drive whatever arrived instead. And a retry
  // cannot help: the same `Idempotency-Key` replays the same URL-less checkout,
  // so an enabled button here would be a control that does nothing, which is the
  // worst thing to hand a guest holding a bill.

  if (failure?.kind === 'no_redirect') {
    return notice(
      'warning',
      '!',
      t('q8.noRedirectTitle'),
      <>
        <p>{t('q17.payAtCounter')}</p>
        <p className="pk-muted">{t('common.askStaff')}</p>
      </>,
      backToBill,
    );
  }

  // --- ReservePay did not answer, and a checkout may exist ------------------
  //
  // The one failure with NO way forward on this screen. There is deliberately no
  // retry: the contract's instruction is "do not retry — this order must be
  // reconciled", and a second attempt is how one bill gets two checkouts.
  //
  // The glyph is a question mark and the ring is Dried Chili on purpose: the
  // honest content of this state is *nobody knows yet*, and unlike the two
  // above it needs a person before the guest does anything else.

  if (failure?.kind === 'reconcile') {
    return notice(
      'error',
      '?',
      t('q8.reconcileTitle'),
      <p className="pk-strong">{t('q8.reconcileBody')}</p>,
      backToBill,
    );
  }

  // --- loading --------------------------------------------------------------
  //
  // At the geometry of the amount card that replaces it, so the figure does not
  // arrive by pushing the page around. Static, never shimmering, and the same
  // grey as `index.html`'s pre-bundle skeleton — a second loading look on one
  // page reads as two pages loading (foundations §10).

  if (loading && !order) {
    return frame(
      <div role="status" aria-busy="true" aria-label={t('common.loading')}>
        <Card>
          <SplitRow>
            <SkeletonBar width="9rem" />
            <SkeletonBar width="6rem" height="28px" />
          </SplitRow>
        </Card>
      </div>,
    );
  }

  if (!order) {
    return notice(
      'warning',
      '!',
      t('q17.notFound'),
      <p className="pk-muted">{t('q17.notFoundAction')}</p>,
    );
  }

  // --- nothing left to pay --------------------------------------------------
  //
  // The one branch on this screen that can be green, and only because the server
  // said so: `orderTotalTone` returns `verified` for a settled bill and nothing
  // else, and the card follows the same decision rather than making its own.

  if (balanceSatang <= 0) {
    return frame(
      <>
        <Card tone={isVerifiedPaid(order) ? 'verified' : 'default'}>
          <Stack>
            <p className="pk-strong">{t('q8.nothingToPay')}</p>
            <SplitRow>
              <span className="pk-muted">{t('common.total')}</span>
              <Money
                satang={order.totals.total_satang}
                tone={orderTotalTone(order)}
                size="title"
              />
            </SplitRow>
          </Stack>
        </Card>
        {backToBill}
      </>,
    );
  }

  // --- the checkout -------------------------------------------------------

  const resumable = attempt?.redirect_url && !isExpired(attempt, now);

  return frame(
    <>
      <Card>
        <SplitRow>
          <span className="pk-muted">{t('q8.amountDue')}</span>
          {/*
            `pending`, from the order. A bill that owes money is never green, and
            starting a checkout does not change that — a checkout being started
            is not money. `Money` renders it through the one formatter in
            `src/i18n/format.ts`, so a balance cannot come out with decimals
            here and without them on the bill.
          */}
          <Money satang={balanceSatang} tone={orderTotalTone(order)} size="title" />
        </SplitRow>
      </Card>
      {order.order_number ? (
        <p className="pk-hint">{t('q17.orderNumber', {number: order.order_number})}</p>
      ) : null}

      {/* Disclosure one of three: what the button does, in ink, at body weight. */}
      <p>{t('q8.handoffNote')}</p>
      <p className="pk-hint">{t('q8.methodsNote')}</p>

      {/* Live region: every failure and the countdown are announced without
          moving focus off the button the guest is about to press. */}
      <div role="status" aria-live="polite" className="pk-stack">
        {resumable && remaining !== null ? (
          <p className="pk-muted">
            {remaining < 60
              ? t('q8.expiresSoon')
              : t('q8.expiresIn', {minutes: Math.ceil(remaining / 60)})}
          </p>
        ) : null}

        {/* Not an error: the venue re-priced the bill and the guest needs to see
            the new figure before paying it. Palm Sugar is the "needs attention,
            nothing is broken" colour on this surface. */}
        {failure?.kind === 'amount_changed' ? (
          <p className="q8-warning">{t('q8.amountChanged')}</p>
        ) : null}
        {failure?.kind === 'generic' ? (
          <p className="q8-alert">{t(failure.messageKey)}</p>
        ) : null}
      </div>

      {/*
        The one solid Chili Red on the screen. Never disabled after a failure —
        that is the moment a guest gives up and waves — except while a request is
        genuinely in flight, and in the `reconcile` state above, which does not
        render this button at all.

        No `disabledReason`: the label *is* the reason. While `starting` the
        button reads "opening the payment page", which is both the accessible
        name and the explanation, and a described-by line repeating it under the
        button would be the same sentence twice.
      */}
      <Button
        variant="primary"
        onClick={() => void pay()}
        disabled={starting}
        aria-busy={starting}
      >
        {starting ? t('q8.paying') : resumable ? t('q8.resume') : t('q8.pay')}
      </Button>

      {/* Disclosures two and three. The package says whose page it is and offers
          the counter, in that order and at two different weights, and both lines
          survive translation and editing — see the file header. */}
      <p className="pk-hint q8-center">{t('q8.leavingNote')}</p>
      <p className="pk-muted q8-center">{t('q8.payAtCounterInstead')}</p>

      {attempt?.checkout_session_id ? (
        <ActionLink to={href(ROUTES.paymentReturn, {orderId})} variant="tertiary" block>
          {t('q9.returnNoticeAction')}
        </ActionLink>
      ) : null}
    </>,
  );
}

/**
 * Six rules this screen needs and the shared sheet does not carry.
 *
 * `href` + `precedence` are React 19's stylesheet de-duplication, the same
 * mechanism `UiStyles` uses: rendered from three different branches of this
 * component, hoisted into `<head>` exactly once. Tokens only — no hex literal,
 * no size below the 16px floor, nothing that re-declares a line-height.
 */
function ScreenStyles() {
  return (
    <style href="pokpok-q8" precedence="pokpok-ui">
      {CSS}
    </style>
  );
}

const CSS = `
/* The package draws this screen as one column with the disclosure pushed to the
   bottom of the viewport, and no pinned bar. pk-body--grow already makes the
   body the growing child; this makes it a column so margin-top:auto on the last
   line has something to push against. */
.q8-body {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
}
.q8-foot {
  margin-top: auto;
  padding-top: var(--pokpok-space-4);
  text-align: center;
}
.q8-center {
  text-align: center;
}
/* Colour never carries the meaning on its own — both of these are a sentence
   that says what happened and what to do. Dried Chili for a request that
   failed; Palm Sugar for a bill that moved, which is not a failure at all. */
.q8-alert {
  color: var(--color-error);
  font-weight: 600;
}
.q8-warning {
  color: var(--color-warning);
  font-weight: 600;
}
`;
